import { clx } from "@hm/fundamental"
import { type MutableRefObject, forwardRef, useCallback, useRef } from "react"

import type { ControlledDatepickerProps } from "../../model/types"
import { CalendarNavigation } from "./calendar-navigation"
import { ContentSwitch } from "./content-switch"
import { DatepickerTrigger } from "./datepicker-trigger"
import { DaysGrid } from "./days-grid"
import { MonthsGrid } from "./months-grid"
import { RangeToggle } from "./range-toggle"
import { useControlledDatepicker } from "./useControlledDatepicker"

const serializeDateValue = (
	value: Date | [Date | null, Date | null] | null,
): string => {
	if (!value) return ""
	if (value instanceof Date) return value.toISOString()
	return JSON.stringify(value.map(d => d?.toISOString() ?? null))
}

export const ControlledDatepicker = forwardRef<
	HTMLInputElement,
	ControlledDatepickerProps
>(
	(
		{
			name,
			label,
			value,
			onChange,
			onBlur,
			showMonthYearPicker,
			language,
			disabled,
			monthsTabLabel = "Months selection",
			daysTabLabel = "Days selection",
			selectRangeLabel = "Select range",
			"data-cy": dataCy = "controlled-datepicker",
		},
		externalRef,
	) => {
		const hiddenInputRef = useRef<HTMLInputElement>(
			null,
		) as MutableRefObject<HTMLInputElement | null>

		const setHiddenRef = useCallback(
			(el: HTMLInputElement | null) => {
				hiddenInputRef.current = el
				if (typeof externalRef === "function") externalRef(el)
				else if (externalRef) externalRef.current = el
			},
			[externalRef],
		)

		const handleChange = useCallback(
			(newValue: Date | [Date | null, Date | null] | null) => {
				onChange(newValue)

				if (hiddenInputRef.current) {
					const nativeInputValueSetter =
						Object.getOwnPropertyDescriptor(
							HTMLInputElement.prototype,
							"value",
						)?.set
					nativeInputValueSetter?.call(
						hiddenInputRef.current,
						serializeDateValue(newValue),
					)
					hiddenInputRef.current.dispatchEvent(
						new Event("change", { bubbles: true }),
					)
				}
			},
			[onChange],
		)

		const {
			isOpen,
			viewMode,
			isRangeMode,
			currentYear,
			currentMonth,
			displayValue,
			weekdays,
			monthNames,
			daysGrid,
			containerRef,
			triggerRef,
			toggleOpen,
			handleSetViewMode,
			handlePrev,
			handleNext,
			handleDayClick,
			handleMonthClick,
			handleRangeToggle,
			clearHoveredDate,
			handleDayHover,
			handleMonthHover,
			isDaySelected,
			isDayInRange,
			isMonthSelected,
			isMonthInRange,
		} = useControlledDatepicker({
			value,
			onChange: handleChange,
			showMonthYearPicker,
			language,
		})

		return (
			<div ref={containerRef} className="relative" data-cy={dataCy}>
				<input
					type="hidden"
					ref={setHiddenRef}
					name={name}
					value={serializeDateValue(value)}
					onBlur={onBlur}
				/>
				<DatepickerTrigger
					name={name}
					label={label}
					displayValue={displayValue}
					isOpen={isOpen}
					disabled={disabled}
					triggerRef={triggerRef}
					onToggle={toggleOpen}
					data-cy={`${dataCy}-trigger`}
				/>

				{isOpen && (
					<dialog
						open
						className={clx(
							"absolute z-50 mt-1 w-[340px] rounded-lg border shadow-lg",
							"border-border bg-container_background",
							"p-4",
						)}
						aria-label={label}
						data-cy={`${dataCy}-dropdown`}
					>
						<ContentSwitch
							viewMode={viewMode}
							onSetViewMode={handleSetViewMode}
							monthsTabLabel={monthsTabLabel}
							daysTabLabel={daysTabLabel}
							data-cy={dataCy}
						/>

						<CalendarNavigation
							viewMode={viewMode}
							currentYear={currentYear}
							currentMonth={currentMonth}
							monthNames={monthNames}
							onPrev={handlePrev}
							onNext={handleNext}
							data-cy={dataCy}
						/>

						{viewMode === "days" ? (
							<DaysGrid
								weekdays={weekdays}
								daysGrid={daysGrid}
								isDaySelected={isDaySelected}
								isDayInRange={isDayInRange}
								onDayClick={handleDayClick}
								onDayHover={handleDayHover}
								onMouseLeave={clearHoveredDate}
								data-cy={dataCy}
							/>
						) : (
							<MonthsGrid
								monthNames={monthNames}
								isMonthSelected={isMonthSelected}
								isMonthInRange={isMonthInRange}
								onMonthClick={handleMonthClick}
								onMonthHover={handleMonthHover}
								onMouseLeave={clearHoveredDate}
								data-cy={dataCy}
							/>
						)}

						<RangeToggle
							isRangeMode={isRangeMode}
							onToggle={handleRangeToggle}
							label={selectRangeLabel}
							data-cy={dataCy}
						/>
					</dialog>
				)}
			</div>
		)
	},
)

ControlledDatepicker.displayName = "ControlledDatepicker"
